import { useState } from "react"
import EditDepartmentPage from "@/routes/_home/user-role-management/department/edit"
import { DepartmentType } from "@/types"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import {
  validateDeleteConsumerPermission,
  validateUserDeletePermission,
} from "@/lib/api/auth"
import { deleteDepartment } from "@/lib/api/billing"
import { toast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Icons } from "@/components/icons"

interface DataProps {
  data: DepartmentType
}

export function DepartmentOperation({ data }: DataProps) {
  const [showDropDown, setShowDropDown] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const queryClient = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: deleteDepartment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departmentDetails"] })
    },
    onError: (error) => {
      alert(`Error deleting item: ${error.message}`)
    },
  })

  return (
    <div>
      <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
        <DropdownMenuTrigger>
          <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
            <Icons.ellipsis className="size-4" />
            <span className="sr-only">Open</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault() // Prevent dropdown from closing immediately
              setShowEditModal(true)
              setShowDropDown(false)
            }}
          >
            <Icons.edit className="mr-2 size-4" />
            Edit Department
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Icons.copy className="mr-2 size-4" />
            Copy Department
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Icons.paste className="mr-2 size-4" />
            Paste Department
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={(event) => {
              validateUserDeletePermission()
              event.preventDefault()
              setShowDeleteDialog(true)
              setShowDropDown(false)
            }}
          >
            <Icons.archive className="mr-2 size-4" />
            Archive
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Credenza open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <CredenzaContent>
          <CredenzaHeader>
            <CredenzaTitle>Confirm Deletion</CredenzaTitle>
          </CredenzaHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete Id {data.id}?
          </p>
          <CredenzaFooter>
            <Button
              variant="destructive"
              onClick={() => {
                if (data.id !== undefined) {
                  deleteMutation.mutate(data.id)
                } else {
                  console.error("Error: data.id is undefined")
                }
                setShowDeleteDialog(false)
                toast({
                  title: "Department Deleted",
                  description: `ID ${data.id} ${data.name} has been deleted.`,
                  duration: 3000,
                })
              }}
            >
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
          </CredenzaFooter>
        </CredenzaContent>
      </Credenza>

      <Credenza open={showEditModal} onOpenChange={setShowEditModal}>
        <EditDepartmentPage
          departmentData={{ ...data }}
          setShowEditModal={setShowEditModal}
        />
      </Credenza>
    </div>
  )
}
